import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from '@angular/common/http';
import {
  DownloadService, ErrorService, KeycloakLv1871Service, TranslationService, UiConfigService,
  RuntimeContext, NotificationService, Notification
} from '@lv1871/core';
import {EMPTY, interval, Observable, of, ReplaySubject, Subscription, throwError} from 'rxjs';
import {catchError, filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {EventService} from './event.service';
import {UiControl} from '../models/uiControl';
import {ButtonControl, flatMap, LesebestaetigungControl, UIArtefakt, UIContainerControl, UiInputControl} from '../models/uiArtefakt';
import {ExternalResourceDialogComponent} from '../components/popupDialog/external-resource-dialog/external-resource-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SanitizationService} from './sanitization.service';
import {Dokument} from '../models/dokument';
import {Command} from '../models/command';
import {getAllMenuStateSeiten} from '../models/menuState';
import {UIArtefaktTyp} from '../models/uiArtefaktTyp';
import {SpeichernDialogComponent, SpeichernDialogInput} from '../components/popupDialog/speichern-dialog/speichern-dialog.component';
import {EnumGruppeTechnischeSteuerung, hasAvvEinwilligung} from '../models/enumGruppeTechnischeSteuerung';
import {ValidationService} from './validation.service';
import {ShellConfigService} from './shell-config.service';
import {CommandRequest, CommandResponse, ProcessRestService} from './rest/process-rest.service';
import {EnumButtonAktionsTyp} from '../models/enumButtonAktionsTyp';

export interface DownloadButtonData {
  buttonCntrl: ButtonControl;
  aktuelleDokumentliste?: Dokument[];
}

@Injectable()
export class


ButtonCommandService implements OnDestroy {

  private _commands$ = new ReplaySubject<string>(1);
  private _downloadCommands$ = new ReplaySubject<DownloadButtonData>(1);
  private _processId: string;
  private _uiControl: UiControl;
  private _uiControlsRequestPending: boolean;
  private _uiControlsRequestPendingSubscription$ = Subscription.EMPTY;
  private _executionResponses$ = this._commands$.pipe(
      switchMap((commandName: string) => interval(100).pipe(
          filter(_ => !this._uiControlsRequestPending),
          take(1),
          switchMap(_ => this._doExecutionCall(commandName))))
  );
  private _downloadResponses$ = this._downloadCommands$.pipe(
      switchMap((data: DownloadButtonData) => interval(100).pipe(
          filter(_ => !this._uiControlsRequestPending),
          take(1),
          switchMap(_ => this._doDownloadCall(data))))
  );

  constructor(private _http: HttpClient, private _eventService: EventService, private _shellConfigService: ShellConfigService,
              private _downloadService: DownloadService, public dialog: MatDialog,
              private _errorService: ErrorService, private _uiConfigService: UiConfigService, private _sanitizer: SanitizationService,
              private matDialog: MatDialog, private _validationService: ValidationService,
              private _authService: KeycloakLv1871Service, private _translationService: TranslationService,
              private _notificationService: NotificationService,
              private _processRestService: ProcessRestService) {
    this._eventService.uiControls$.subscribe(uiControl => this._uiControl = uiControl);
    this._eventService.uiControlsNewst$.pipe(
        filter((uiControl: UiControl) => !!uiControl && !!uiControl.processId))
        .subscribe(uiControl => this._processId = uiControl.processId);
    this._uiControlsRequestPendingSubscription$ = this._eventService.uiControlsRequestPending$
        .subscribe(requestPending => this._uiControlsRequestPending = requestPending);
  }

  public ngOnDestroy() {
    if (!!this._uiControlsRequestPendingSubscription$) {
      this._uiControlsRequestPendingSubscription$.unsubscribe();
    }
  }

  handleCommand(buttonCntrl: ButtonControl, aktuelleDokumentliste?: Dokument[]): Observable<any> {
    if (buttonCntrl.Command === 'DokumentDownload') {
      const data = {} as DownloadButtonData;
      data.aktuelleDokumentliste = aktuelleDokumentliste;
      data.buttonCntrl = buttonCntrl;
      this._downloadCommands$.next(data);
      return this._downloadResponses$.pipe(take(1));
    } else {
      this._commands$.next(buttonCntrl.Command);
      return this._executionResponses$.pipe(take(1));
    }
  }

  postCommand(command: string): Observable<any> {
    this._commands$.next(command);
    return this._executionResponses$.pipe(take(1));
  }

  handleLesebestaetigungCommand(lesebestaetigungControl: LesebestaetigungControl): Observable<boolean> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._eventService.uiControls$.pipe(
        take(1),
        switchMap((uiControl) =>
            this._http.post(this._buildDokumentUrl(lesebestaetigungControl.DokumentPaketTyp),
                {
                  uiControl: this._sanitizer.sanitize(uiControl)
                },
                {headers, responseType: 'blob'})
                .pipe(
                    map(blob => {
                      const filename = lesebestaetigungControl.DokumentPaketTyp + '.pdf';
                      this.openModalDialog(filename, blob);
                      return true;
                    }),
                    catchError((error) => {
                      this._errorService.pushErrorMessage(error.message);
                      return of(false);
                    }))
        )
    );
  }

  handleLoadDokumentPaketCommand(buttonControl: ButtonControl): Observable<Dokument[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._eventService.uiControls$.pipe(
        take(1),
        filter(uiControl => !!uiControl.uiVorgang.AngebotserstellungMoeglichJN),
        tap(_ => this._eventService.isDokumentpaketRequestPending$.next(true)),
        switchMap((uiControl) =>
            this._http.post<Dokument[]>(this._buildDokumenPakettUrl(buttonControl.DokumentPaketTyp),
                {
                  uiControl: this._sanitizer.sanitize(uiControl)
                },
                {headers})
                .pipe(
                    tap(_ => this._eventService.isDokumentpaketRequestPending$.next(false)),
                    catchError((error) => {
                      this._errorService.pushErrorMessage(error.message);
                      this._eventService.isDokumentpaketRequestPending$.next(false);
                      return EMPTY;
                    }))
        )
    );
  }

  public handleActionButtonAktionstyp(buttonAktionstyp: EnumButtonAktionsTyp): Observable<boolean> {
    if (!!buttonAktionstyp) {
      switch (buttonAktionstyp) {
        case EnumButtonAktionsTyp.ELEKTRONISCHUNTERSCHREIBEN_MOEGLICH:
          flatMap(this._uiControl.uiVorgang.UIArtefakt)
              .filter(artefakt => artefakt.FeldnameExtern === '/wmAntragEinreichen')
              .map(artefakt => {
                artefakt.WertEingabe = 'ElektronischUnterschreiben';
              });
          this._eventService.uiControls$.next(this._uiControl);
          this._eventService.uiControlsNewst$.next(this._uiControl);
          return of(true);
        case EnumButtonAktionsTyp.ELEKTRONISCHUNTERSCHREIBEN_NICHT_MOEGLICH:
          flatMap(this._uiControl.uiVorgang.UIArtefakt)
              .filter(artefakt => artefakt.FeldnameExtern === '/wmAntragEinreichen')
              .map(artefakt => {
                artefakt.WertEingabe = 'DokumenteHerunterladen';
              });
          this._eventService.uiControls$.next(this._uiControl);
          this._eventService.uiControlsNewst$.next(this._uiControl);
          return of(true);
      }
    }
  }

  public openModalDialog(filename: string, blob: Blob): void {
    const dialogData = {
      blob,
      filename,
      header: filename
    };
    const dialogRef = this.dialog.open(ExternalResourceDialogComponent, {
      panelClass: 'lv-external-resource-dialog',
      data: dialogData
    });
    /* ToDo: für responsiveness beachten
    this._infoDialogSubscription = dialogRef.afterClosed().subscribe();
    if (this._isScreenSizeMobile) {
      const dialog = document.getElementsByClassName('lv-external-resource-dialog')[0] as HTMLElement;
      dialog.style.maxWidth = 'unset';
      dialog.style.width = '98%';
      dialog.style.height = '98%';
    }*/
  }

  private _doDownloadCall(data: DownloadButtonData): Observable<any> {
    return this._eventService.uiControls$.pipe(
        take(1),
        switchMap((uiControl) =>
            this._http.post(this._buildDokumentUrl(data.buttonCntrl.DokumentPaketTyp),
                {
                  uiControl: this._sanitizer.sanitize(uiControl),
                  uiDokumentliste: data.aktuelleDokumentliste
                },
                {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'blob' as 'json', observe: 'response'})
                .pipe(
                    tap((httpResponse: HttpResponse<Blob>) => {
                      const filename = httpResponse.headers.get('Content-Disposition');
                      if (data.buttonCntrl.DownloadTyp === 'Download') {
                        this._downloadService.saveOrOpenFile(filename, httpResponse.body);
                      } else if (data.buttonCntrl.DownloadTyp === 'NeuerTab') {
                        this._downloadService.openFileInNewTab(filename, httpResponse.body);
                      } else if (data.buttonCntrl.DownloadTyp === 'ModalDialog') {
                        this.openModalDialog(filename, httpResponse.body);
                      }
                    }),
                    catchError((error) => {
                      if (typeof (error.error) === 'string') {
                        this._errorService.pushErrorMessage(error.error);
                      } else {
                        this._errorService.pushErrorMessage(error.message);
                      }
                      return of(null);
                    })))
    );
  }

  private _doExecutionCall(commandName: string): Observable<any> {
    const command = commandName.charAt(0).toLowerCase() + commandName.slice(1);
    const executionCallObs$ = this._eventService.uiControls$.pipe(
        take(1),
        map(uiControl => ({
          uiControl,
          clientUrl: window.location.origin
        } as CommandRequest)),
        switchMap((request) => this._processRestService.postCommand(command, this._processId, request)
            .pipe(
                filter(response => !!response),
                tap((response: CommandResponse) => {
                  if (!!response.redirectUrl) {
                    this._eventService.supressBeforeUnloadDialogButtonClicked$.next();
                    window.location.replace(response.redirectUrl);
                    return;
                  }
                  this._eventService.uiControls$.next(response.uiControl);
                  this._eventService.uiControlsNewst$.next(response.uiControl);
                  this._eventService.uiControlsUnmodified$.next(JSON.parse(JSON.stringify(response.uiControl)));
                }),
                catchError(this._handleError())))
    );

    if (commandName === Command.SPEICHERN || commandName === Command.SPEICHERN_SB_SHELL_UI) {
      return this._eventService.uiControls$.pipe(
          take(1),
          map(uiControl => {
            const speichernDialoge = flatMap(uiControl.uiVorgang.UIArtefakt)
                .filter(artefakt => artefakt.UIArtefakttyp === UIArtefaktTyp.UI_CONTAINER
                    && (artefakt as UIContainerControl).UIContainerComponent === 'SpeichernDialog');
            if (!!speichernDialoge.length) {
              return speichernDialoge[0];
            }
            return null;
          }),
          switchMap((speichernDialog: UIArtefakt) => {
            if (!!speichernDialog && this._shellConfigService.getParameter().runtimeContext !== RuntimeContext.SB_SHELL) {
              if (this._authService.isAuthenticated() &&
                  this._shellConfigService.getParameter().runtimeContext !== RuntimeContext.STANDALONE &&
                  this._eventService.vermittlerdatenFuerAvvGesetzt$ &&
                  !!this._eventService.avvEinwilligungImAntrag$.getValue() &&
                  !this._eventService.avvEinwilligungImAntrag$.getValue().WertEingabe) {
                const notification = new Notification(
                    'Alert', 'Danger', '',
                    this._translationService.translate('ERRORS.SPEICHERN_AVV_EINWILLIGUNG'), true, null);
                this._notificationService.addNotification(notification);
                return of(null);
              }
              if (flatMap(speichernDialog.UIArtefakt)
                  .filter(artefakt => !!(artefakt as UiInputControl).PflichtfeldJN && !artefakt.WertEingabe).length) {
                return this.matDialog.open<SpeichernDialogComponent, SpeichernDialogInput, boolean>(SpeichernDialogComponent, {
                  data: {artefakte: speichernDialog.UIArtefakt} as SpeichernDialogInput,
                  panelClass: 'lv-modal-dialog',
                  disableClose: true,
                  autoFocus: false
                }).afterClosed().pipe(switchMap(success => !!success ? executionCallObs$ : of(null)));
              }
            }
            return executionCallObs$;
          }));
    } else if (commandName === Command.BEANTRAGEN || commandName === Command.ELEKTRONISCH_UNTERSCHREIBEN) {
      this._eventService.supressBeforeUnloadDialogButtonClicked$.next();
      if (commandName === Command.ELEKTRONISCH_UNTERSCHREIBEN) {
        if (!hasAvvEinwilligung(true,
            this._eventService.avvEinwilligungImAntrag$.getValue(),
            this._eventService.vermittlerdatenFuerAvvGesetzt$.getValue())) {
          if (!!this._eventService.avvEinwilligungImAntrag$.getValue() &&
              !this._eventService.avvEinwilligungImAntrag$.getValue().WertEingabe) {
            const notification = new Notification(
                'Alert', 'Danger', '',
                this._translationService.translate('ERRORS.AVV_EINWILLIGUNG_FEHLT'), true, null);
            this._notificationService.addNotification(notification);
          }
          if (!this._eventService.vermittlerdatenFuerAvvGesetzt$.getValue()) {
            const notification = new Notification(
                'Alert', 'Danger', '',
                this._translationService.translate('ERRORS.VERMITTLER_DATEN_FEHLEN'), true, null);
            this._notificationService.addNotification(notification);
          }
          return of(null);
        } else {
          if (!this._eventService.vermittlerdatenFuerAvvGesetzt$.getValue() &&
              this._shellConfigService.getParameter().runtimeContext !== RuntimeContext.STANDALONE) {
            const notification = new Notification(
                'Alert', 'Danger', '',
                'ERRORS.VERMITTLER_DATEN_FEHLEN', true, null);
            this._notificationService.addNotification(notification);
            return of(null);
          }
        }
      }
      return this._eventService.validationState$.pipe(
          take(1),
          withLatestFrom(this._eventService.menuItemSelected$, this._eventService.menuState$),
          tap(([_, menuItemSelected, __]) => this._eventService.validateSeiteSelected$.next(menuItemSelected)),
          map(([_, menuItemSelected, menuState]) => {
            const menuSeiten = getAllMenuStateSeiten(menuState);
            const selectedMenuSeite = menuSeiten.filter(seite => seite.Identifikator === menuItemSelected)[0];
            return selectedMenuSeite?.valid;
          }),
          switchMap(isValid => isValid ? executionCallObs$ : of(null))
      );
    } else if (commandName === Command.ANGEBOT) {
      const commandEnum = commandName.slice(0);
      if ((Object as any).values(EnumGruppeTechnischeSteuerung).includes(commandEnum)) {
        return this._eventService.uiControls$.pipe(
            take(1),
            switchMap(uiControl => {
              const isValid = this._validationService.validateGruppeTechnischeSteuerung(
                  uiControl.uiVorgang.UIArtefakt, commandEnum as EnumGruppeTechnischeSteuerung);
              return isValid ? executionCallObs$ : of(null);
            })
        );
      }
    }
    return executionCallObs$;
  }

  private _buildDokumentUrl(dokumentName: string): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/process/dokument/' + dokumentName + '/file';
  }

  private _buildDokumenPakettUrl(dokumentName: string): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/dokumentpaket/' + dokumentName + '/list';
  }


  private _handleError(): (errorResponse: HttpErrorResponse) => Observable<void> {
    return (errorResponse: HttpErrorResponse) => {
      if (typeof (errorResponse.error) === 'string') {
        this._errorService.pushErrorMessage(errorResponse.error);
      } else {
        this._errorService.pushErrorMessage(errorResponse.message);
      }
      return throwError(() => errorResponse.status);
    };
  }
}
